"use client";

import Link from "next/link";
import AuthApiCheck from "@/components/auth-api-check";
import {
  Activity,
  Bot,
  CalendarCheck,
  ChevronDown,
  CircleHelp,
  CreditCard,
  Globe,
  LayoutDashboard,
  MessageSquare,
  Plus,
  Settings,
  Users,
  Zap,
  ArrowUpRight,
  TrendingUp,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";

const navigation = [
  { label: "Overview", icon: LayoutDashboard, active: true },
  { label: "AI Assistants", icon: Bot },
  { label: "Knowledge Base", icon: Globe },
  { label: "Conversations", icon: MessageSquare },
  { label: "Channels", icon: Activity },
  { label: "Bookings", icon: CalendarCheck },
];

const management = [
  { label: "Team Members", icon: Users },
  { label: "Usage & Billing", icon: CreditCard },
  { label: "Settings", icon: Settings },
];

const stats = [
  {
    label: "AI Assistants",
    value: "3",
    note: "2 active",
    icon: Bot,
    color: "bg-blue-50 text-blue-600",
  },
  {
    label: "Conversations",
    value: "1,284",
    note: "+12.8% this month",
    icon: MessageSquare,
    color: "bg-violet-50 text-violet-600",
  },
  {
    label: "Bookings Generated",
    value: "86",
    note: "+8.2% this month",
    icon: CalendarCheck,
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    label: "AI Usage",
    value: "68%",
    note: "Monthly allowance",
    icon: Zap,
    color: "bg-amber-50 text-amber-600",
  },
];

const activities = [
  {
    title: "New conversation received",
    detail: "Website chatbot · 2 minutes ago",
    status: "New",
    color: "bg-blue-100 text-blue-700",
  },
  {
    title: "Booking inquiry processed",
    detail: "Facebook Messenger · 18 minutes ago",
    status: "Processed",
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    title: "Knowledge base updated",
    detail: "Hotel information · 1 hour ago",
    status: "Updated",
    color: "bg-violet-100 text-violet-700",
  },
  {
    title: "AI assistant activated",
    detail: "Customer Service Assistant · Yesterday",
    status: "Active",
    color: "bg-emerald-100 text-emerald-700",
  },
];

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#f6f8fc] text-slate-800">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-[#101b35] text-white lg:flex">
        <div className="flex h-20 items-center gap-3 border-b border-white/10 px-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500">
            <Bot size={24} />
          </div>
          <div>
            <div className="text-lg font-bold tracking-tight">Webspider AI</div>
            <div className="text-xs text-slate-400">Business Platform</div>
          </div>
        </div>

        <div className="px-4 py-6">
          <div className="mb-3 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Workspace
          </div>

          <nav className="space-y-1">
            {navigation.map((item) => (
              <Link
                href={item.label === "AI Assistants" ? "/assistants" : "#"}
                key={item.label}
                className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition ${
                  item.active
                    ? "bg-blue-600 font-semibold text-white"
                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <item.icon size={18} />
                {item.label}
                {item.label === "AI Assistants" && (
                  <span className="ml-auto rounded-md bg-white/15 px-2 py-0.5 text-xs">
                    3
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="mb-3 mt-9 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Management
          </div>

          <nav className="space-y-1">
            {management.map((item) => (
              <Link
                href={item.label === "AI Assistants" ? "/assistants" : "#"}
                key={item.label}
                className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-slate-300 transition hover:bg-white/10 hover:text-white"
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-auto border-t border-white/10 p-4">
          <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/20 text-blue-300">
              <CircleHelp size={19} />
            </div>
            <div>
              <div className="text-sm font-semibold">Need assistance?</div>
              <div className="text-xs text-slate-400">Visit our help center</div>
            </div>
          </div>
          <div className="mt-5 text-center text-[10px] text-slate-500">
            Webspider AI © 2026
          </div>
        </div>
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-[#101b35] p-6 text-white lg:hidden">
          <div className="mb-8 flex items-center justify-between">
            <span className="text-xl font-bold">Webspider AI</span>
            <button onClick={() => setMenuOpen(false)}>
              <X size={24} />
            </button>
          </div>
          {[...navigation, ...management].map((item) => (
            <Link
              key={item.label}
              href={item.label === "AI Assistants" ? "/assistants" : "#"}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 border-b border-white/10 py-4"
            >
              <item.icon size={19} />
              {item.label}
            </Link>
          ))}
        </div>
      )}

      <main className="min-h-screen lg:ml-64">
        <header className="sticky top-0 z-20 flex h-20 items-center justify-between border-b border-slate-200 bg-white/90 px-5 backdrop-blur md:px-9">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={23} />
            </button>
            <div>
              <div className="text-sm text-slate-500">Workspace / Overview</div>
              <div className="text-lg font-bold text-slate-900">
                Dashboard
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold">Demo Business</div>
              <div className="text-xs text-slate-500">Business workspace</div>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">
              DB
            </div>
            <ChevronDown size={16} className="text-slate-400" />
          </div>
        </header>

        <div className="mx-auto max-w-[1500px] space-y-8 p-5 md:p-9">
          <section className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
            <div>
              <div className="text-sm font-medium text-blue-600">
                Wednesday, November 4, 2026
              </div>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                Welcome to Webspider AI
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Manage your AI assistants and business conversations in one place.
              </p>
            </div>

            <button className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700">
              <Plus size={18} />
              Create AI Assistant
            </button>
          </section>

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">
                    {stat.label}
                  </span>
                  <div className={`rounded-xl p-3 ${stat.color}`}>
                    <stat.icon size={20} />
                  </div>
                </div>
                <div className="mt-4 text-3xl font-bold tracking-tight text-slate-900">
                  {stat.value}
                </div>
                <div className="mt-2 text-xs text-slate-500">{stat.note}</div>
              </div>
            ))}
          </section>

          <section className="grid gap-6 xl:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm xl:col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-slate-900">
                    Conversation Overview
                  </h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Sample activity for the last seven days
                  </p>
                </div>
                <span className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600">
                  Last 7 days
                </span>
              </div>

              <div className="mt-8 flex h-48 items-end justify-between gap-3">
                {[42, 65, 50, 82, 60, 90, 72].map((height, index) => (
                  <div
                    key={index}
                    className="flex h-full flex-1 flex-col justify-end gap-2"
                  >
                    <div
                      className="w-full rounded-t-lg bg-blue-500 transition hover:bg-blue-600"
                      style={{ height: `${height}%` }}
                    />
                    <div className="text-center text-xs text-slate-400">
                      {["Thu", "Fri", "Sat", "Sun", "Mon", "Tue", "Wed"][index]}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs text-emerald-600">
                <TrendingUp size={15} />
                Sample analytics — live data integration pending
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="font-bold text-slate-900">Quick Actions</h2>
              <p className="mt-1 text-xs text-slate-500">
                Common workspace tasks
              </p>

              <div className="mt-6 space-y-3">
                {[
                  ["Create AI Assistant", Bot],
                  ["Upload Knowledge", Globe],
                  ["View Conversations", MessageSquare],
                  ["Manage Bookings", CalendarCheck],
                ].map(([label, Icon]) => {
                  const ItemIcon = Icon as typeof Bot;
                  return (
                    <button
                      key={label as string}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left text-sm font-medium transition hover:border-blue-300 hover:bg-blue-50"
                    >
                      <ItemIcon size={18} className="text-blue-600" />
                      {label as string}
                      <ArrowUpRight
                        size={15}
                        className="ml-auto text-slate-400"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-slate-900">Recent Activity</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Latest workspace events
                </p>
              </div>
              <button className="text-xs font-semibold text-blue-600">
                View all
              </button>
            </div>

            <div className="mt-5 divide-y divide-slate-100">
              {activities.map((activity) => (
                <div
                  key={activity.title}
                  className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                      <Activity size={19} className="text-slate-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">
                        {activity.title}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {activity.detail}
                      </div>
                    </div>
                  </div>
                  <span
                    className={`w-fit rounded-full px-3 py-1 text-xs font-medium ${activity.color}`}
                  >
                    {activity.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
         <AuthApiCheck />

          <footer className="pb-4 text-center text-xs text-slate-400">
            Webspider AI · Business Automation and AI Platform
          </footer>
        </div>
      </main>
    </div>
  );
}
